import { log, supabaseUrl, supabaseAnonKey, client } from ".";

/**
 * Obtiene actividades CRM de un cliente basándose en los contactos asignados
 * @param {number} clienteId - ID del cliente
 * @param {number} [limit=10] - Cantidad máxima de actividades
 * @param {number|number[]} [contactosAgencia=null] - usuario_id(s) del contacto de agencia
 * @param {number|number[]} [contactosCliente=null] - usuario_id(s) del contacto del cliente
 * @returns {Promise<Array>} Lista de actividades con destinatarios
 */
export const getCrmActividadesByCliente = async (
  clienteId,
  limit = 10,
  contactosAgencia = null,
  contactosCliente = null
) => {
  try {
    const actividadIds = new Set();
    const headers = { apikey: supabaseAnonKey, "Content-Type": "application/json" };

    // Normalizar a arrays
    const agenciaIds = Array.isArray(contactosAgencia)
      ? contactosAgencia.filter(Boolean)
      : contactosAgencia
        ? [contactosAgencia]
        : [];

    const clienteIds = Array.isArray(contactosCliente)
      ? contactosCliente.filter(Boolean)
      : contactosCliente
        ? [contactosCliente]
        : [];

    // Obtener emails de los usuarios
    const allUserIds = [...agenciaIds, ...clienteIds].filter(Boolean);
    const allEmails = [];

    if (allUserIds.length > 0) {
      const { data: users } = await client.from("usuarios").select("id,email").in("id", allUserIds);
      if (users) {
        users.forEach((u) => {
          if (u.email) allEmails.push(u.email.toLowerCase());
        });
      }
    }

    // 1. Buscar actividades directamente vinculadas al cliente
    const directResponse = await fetch(`${supabaseUrl}/rest/v1/crm_actividades?cliente_id=eq.${clienteId}&select=id`, {
      method: "GET",
      headers,
    });
    if (directResponse.ok) {
      const data = await directResponse.json();
      data.forEach((a) => actividadIds.add(a.id));
    }

    // 2. Buscar en destinatarios por usuario_id (query combinada)
    if (allUserIds.length > 0) {
      const userIdResponse = await fetch(
        `${supabaseUrl}/rest/v1/crm_actividades_destinatarios?usuario_id=in.(${allUserIds.join(",")})&select=actividad_id`,
        { method: "GET", headers }
      );
      if (userIdResponse.ok) {
        const data = await userIdResponse.json();
        data.forEach((d) => actividadIds.add(d.actividad_id));
      }
    }

    // 3. Buscar en destinatarios por email (match exacto, case-insensitive)
    if (allEmails.length > 0) {
      const emailFilters = allEmails.map((e) => `email.ilike.${e}`).join(",");
      const emailResponse = await fetch(
        `${supabaseUrl}/rest/v1/crm_actividades_destinatarios?or=(${emailFilters})&select=actividad_id`,
        { method: "GET", headers }
      );
      if (emailResponse.ok) {
        const data = await emailResponse.json();
        data.forEach((d) => actividadIds.add(d.actividad_id));
      }
    }

    // 4. Obtener actividades completas y destinatarios
    let actividades = [];
    const ids = [...actividadIds];

    if (ids.length > 0) {
      const actividadesResponse = await fetch(
        `${supabaseUrl}/rest/v1/crm_actividades?id=in.(${ids.join(",")})&or=(cliente_id.is.null,cliente_id.eq.${clienteId})&order=fecha.desc&limit=${limit}`,
        { method: "GET", headers }
      );

      if (actividadesResponse.ok) {
        actividades = await actividadesResponse.json();
      }

      // Obtener todos los destinatarios en una sola query
      if (actividades.length > 0) {
        const actIds = actividades.map((a) => a.id).join(",");
        const destResponse = await fetch(
          `${supabaseUrl}/rest/v1/crm_actividades_destinatarios?actividad_id=in.(${actIds})`,
          { method: "GET", headers }
        );
        if (destResponse.ok) {
          const allDest = await destResponse.json();
          // Agrupar destinatarios por actividad_id
          actividades.forEach((actividad) => {
            actividad.destinatarios = allDest.filter((d) => d.actividad_id === actividad.id);
          });
        }
      }
    }

    // 5. Obtener ventas cerradas
    const salesResponse = await fetch(
      `${supabaseUrl}/rest/v1/sales?client_id=eq.${clienteId}&estatus=eq.100%25&fecha_cierre=not.is.null&eliminada=eq.false&select=id,fecha_cierre,importe,campana,code`,
      { method: "GET", headers }
    );

    if (salesResponse.ok) {
      const sales = await salesResponse.json();
      const saleActivities = sales.map((sale) => ({
        id: `sale-${sale.id}`,
        tipo: "SALE",
        fecha: sale.fecha_cierre,
        cliente_id: clienteId,
        destinatarios: [],
        metadata: {
          sale_id: sale.id,
          importe: sale.importe,
          campana: sale.campana,
          code: sale.code,
        },
        virtual: true,
      }));
      actividades.push(...saleActivities);
    }

    // Ordenar por fecha descendente
    actividades.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

    return actividades.slice(0, limit);
  } catch (error) {
    log.error("getCrmActividadesByCliente", error);
    throw error;
  }
};

/**
 * Obtiene todas las actividades CRM por país
 * @param {string} pais - País
 * @returns {Promise<Array>} Lista de actividades
 */
export const getCrmActividadesByPais = async (pais) => {
  try {
    const url = `${supabaseUrl}/rest/v1/crm_actividades?pais=eq.${pais}&order=fecha.desc`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        apikey: supabaseAnonKey,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Error fetching CRM actividades by pais");
    }

    return await response.json();
  } catch (error) {
    log.error("getCrmActividadesByPais", error);
    throw error;
  }
};

/**
 * Verifica si existe una actividad SALE para una oportunidad
 * @param {number} oportunidadId - ID de la oportunidad
 * @returns {Promise<boolean>} true si existe
 */
export const existsSaleActivity = async (oportunidadId) => {
  try {
    const url = `${supabaseUrl}/rest/v1/crm_actividades?referencia_id=eq.${oportunidadId}&referencia_tipo=eq.oportunidad&tipo=eq.SALE`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        apikey: supabaseAnonKey,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.length > 0;
  } catch (error) {
    log.error("existsSaleActivity", error);
    return false;
  }
};
